/**
 * Partner request edit data hook.
 *
 * Fetches partner request data for editing forms. Handles different scenarios:
 * creating new requests (add mode), editing specific requests by ID, or editing
 * the first active or most recent request. Determines edit vs create mode based
 * on URL state and available requests.
 *
 * @module usePartnerRequestForEdit
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PartnerRequest } from '@/types/partner-finder';
import { getMyPartnerRequests } from '@/lib/partnerApi';
import { mapApiRequestToPartnerRequest } from '@/lib/mappers';
import { useAsyncData } from './useAsyncData';

interface UsePartnerRequestForEditReturn {
  readonly request: PartnerRequest | null;
  readonly isLoading: boolean;
  readonly isCreatingNew: boolean;
}

/**
 * Hook for fetching partner request data for editing.
 *
 * Handles different scenarios based on URL state and available requests:
 * - Creating new request (add mode): Returns null request with `isCreatingNew: true`
 * - Editing specific request by ID: Returns the specified request
 * - Editing first active or most recent request: Returns the first active request, or most recent if none active
 *
 * @param userId - Current user ID (optional)
 * @returns Object containing:
 *   - `request`: PartnerRequest object to edit, or null if creating new
 *   - `isLoading`: Boolean indicating if data is being fetched
 *   - `isCreatingNew`: Boolean indicating if in create mode (true) or edit mode (false)
 *
 * @example
 * ```typescript
 * const { request, isLoading, isCreatingNew } = usePartnerRequestForEdit(userId);
 * if (isCreatingNew) {
 *   // Show empty form
 * } else {
 *   // Pre-fill form with request data
 * }
 * ```
 */
export function usePartnerRequestForEdit(
  userId?: string,
): UsePartnerRequestForEditReturn {
  const location = useLocation();

  // Fetch requests using useAsyncData
  const { data: requests, isLoading } = useAsyncData<PartnerRequest[]>(
    async () => {
      if (!userId) {
        return [];
      }
      const apiRequests = await getMyPartnerRequests(userId);
      return apiRequests.map(mapApiRequestToPartnerRequest);
    },
    [userId],
  );

  // Determine request and create mode based on fetched data and location state
  const { request, isCreatingNew } = useMemo(() => {
    if (!userId) {
      return { request: null, isCreatingNew: true };
    }

    if (!requests || requests.length === 0) {
      return { request: null, isCreatingNew: true };
    }

    const requestId = location.state?.requestId;
    const isAddMode = location.state?.action === 'add';

    if (isAddMode) {
      // Create mode: no initial data
      return { request: null, isCreatingNew: true };
    } else if (requestId) {
      // Edit specific request by ID
      const requestToEdit = requests.find((r) => r.id === requestId);
      if (requestToEdit) {
        return { request: requestToEdit, isCreatingNew: false };
      } else {
        // Request not found, treat as create
        return { request: null, isCreatingNew: true };
      }
    } else {
      // No request ID specified, try to find first active or most recent
      const activeRequest = requests.find((r) => r.status === 'active') || requests[0];
      if (activeRequest) {
        return { request: activeRequest, isCreatingNew: false };
      } else {
        // No requests, treat as create
        return { request: null, isCreatingNew: true };
      }
    }
  }, [requests, userId, location.state]);

  return { request, isLoading, isCreatingNew };
}

